import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, ShoppingCart, User, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import Logo from './Logo';
import AnchorLink from './AnchorLink';

const CATEGORIES = [
  { id: 'mycelium', label: 'Mycélium & LC' },
  { id: 'substrat', label: 'Substrats & Grain' },
  { id: 'kit', label: 'Kits Prêts-à-Pousser' },
  { id: 'accessoire', label: 'Accessoires Labo' },
];

const ease = [0.22, 1, 0.36, 1];

export const MobileMenu = ({ open, onClose, links }) => {
  const { count, setOpen: setCartOpen } = useCart();
  const [catsOpen, setCatsOpen] = useState(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[95] flex flex-col bg-void/95 backdrop-blur-xl md:hidden"
          data-testid="mobile-menu-overlay"
        >
          <div className="grid-bg absolute inset-0 opacity-40" />

          <div className="relative flex items-center justify-between border-b border-white/10 px-4 py-4">
            <Logo size={30} />
            <button
              onClick={onClose}
              data-testid="mobile-menu-close"
              aria-label="Fermer le menu"
              className="flex h-10 w-10 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-neon/50 hover:text-neon"
            >
              <X size={18} />
            </button>
          </div>

          <nav className="relative flex-1 overflow-y-auto px-6 py-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 font-mono text-[10px] uppercase tracking-[0.35em] text-neon/70"
            >
              Navigation
            </motion.div>

            <div className="space-y-1">
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.45, delay: 0.05, ease }}
                className="border-b border-white/5"
              >
                <div className="flex items-center justify-between">
                  <Link
                    to="/boutique"
                    onClick={onClose}
                    data-testid="mobile-link-boutique"
                    className="flex-1 py-4 font-display text-2xl font-bold tracking-tight text-slate-100 transition-colors hover:text-neon"
                  >
                    Boutique
                  </Link>
                  <button
                    onClick={() => setCatsOpen((v) => !v)}
                    data-testid="mobile-toggle-categories"
                    aria-label="Afficher les catégories"
                    className="flex h-10 w-10 items-center justify-center text-neon"
                  >
                    <ChevronDown size={18} className={`transition-transform duration-300 ${catsOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                <AnimatePresence initial={false}>
                  {catsOpen && (
                    <motion.ul
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease }}
                      className="overflow-hidden pb-3 pl-4"
                      data-testid="mobile-categories-list"
                    >
                      {CATEGORIES.map((c) => (
                        <li key={c.id}>
                          <Link
                            to={`/boutique?cat=${c.id}`}
                            onClick={onClose}
                            data-testid={`mobile-cat-${c.id}`}
                            className="flex items-center gap-2 border-l border-neon/20 py-2.5 pl-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-neon"
                          >
                            <ArrowUpRight size={12} className="text-neon/60" /> {c.label}
                          </Link>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.div>

              {links.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.45, delay: 0.1 + i * 0.06, ease }}
                  className="border-b border-white/5"
                >
                  {l.to ? (
                    <Link
                      to={l.to}
                      onClick={onClose}
                      data-testid={`mobile-link-${l.id}`}
                      className="block py-4 font-display text-2xl font-bold tracking-tight text-slate-100 transition-colors hover:text-neon"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <AnchorLink
                      id={l.href.replace('#', '')}
                      onClick={onClose}
                      data-testid={`mobile-link-${l.id}`}
                      className="block py-4 font-display text-2xl font-bold tracking-tight text-slate-100 transition-colors hover:text-neon"
                    >
                      {l.label}
                    </AnchorLink>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.4, ease }}
              className="mt-10 space-y-3"
            >
              <button
                onClick={() => { onClose(); setCartOpen(true); }}
                data-testid="mobile-open-cart"
                className="clip-frame-sm flex w-full items-center justify-between border border-neon/40 bg-neon/10 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neon transition-colors hover:bg-neon hover:text-void"
              >
                <span className="flex items-center gap-2"><ShoppingCart size={15} /> Mon panier</span>
                <span className="flex h-5 min-w-5 items-center justify-center bg-neon px-1 text-[10px] font-bold text-void">{count}</span>
              </button>
              <Link
                to="/compte"
                onClick={onClose}
                data-testid="mobile-link-account"
                className="clip-frame-sm flex w-full items-center gap-2 border border-white/15 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-neon/50 hover:text-neon"
              >
                <User size={15} /> Mon compte
              </Link>
              <Link
                to="/admin"
                onClick={onClose}
                data-testid="mobile-link-admin"
                className="flex w-full items-center gap-2 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 transition-colors hover:text-cyber"
              >
                <ShieldCheck size={13} /> Espace admin
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600"
            >
              terrafungi.fr — myciculture de précision
            </motion.p>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;
